'use client';

import { useCallback, useState } from 'react';
import type { PropsWithChildren } from 'react';

import classnames from 'classnames';

import { Resource, ResourceBody } from '../../resource';
import {
  AiWarningIcon,
  EditQuestionButton,
  ExplanationButton,
  FeedbackButton,
  PushQuestionButton,
} from '../layout/QuestionControls';
import { QuestionToolbar } from '../layout/QuestionToolbar';
import { useOptimisticBookmark } from '../useOptimisticBookmark';
import type { MultipleChoiceQuestionAnswerProps } from './MultipleChoiceQuestionAnswer.types';
import { Answer } from './layout/Answer';
import { AnswerFooter } from './layout/AnswerFooter';

import styles from './MultipleChoiceQuestionAnswer.module.scss';

function MultipleChoiceQuestionAnswer({
  style,
  className,
  question,
  children,
  defaultShowExplanation = false,
  onBookmark,
  onUnbookmark,
  onFeedback,
  onEdit,
  onPush,
  onClose,
  onExplain,
  testId = 'ui-multiple-choice-question-answer',
}: PropsWithChildren<MultipleChoiceQuestionAnswerProps>) {
  const [showReason, setShowReason] = useState(false);
  const [showExplanation, setShowExplanation] = useState(
    defaultShowExplanation,
  );

  // A new question is answered from the top: what was open about the last one says
  // nothing about this one, and an explanation is only ever fetched on request — so a
  // panel left open would sit there loading forever. Adjusted while rendering rather
  // than in an effect, so the new question is never painted under the old one's panel.
  const [answeredQuestionId, setAnsweredQuestionId] = useState(question.id);

  if (answeredQuestionId !== question.id) {
    setAnsweredQuestionId(question.id);
    setShowReason(false);
    setShowExplanation(defaultShowExplanation);
  }

  const { bookmarked, toggle: toggleBookmark } = useOptimisticBookmark({
    id: question.id,
    bookmarked: question.bookmarked,
    onBookmark,
    onUnbookmark,
  });

  const handleShowReason = useCallback(() => {
    setShowReason((prev) => !prev);
    setShowExplanation(false);
  }, []);

  const handleShowExplanation = useCallback(async () => {
    setShowReason(false);

    if (showExplanation) {
      setShowExplanation(false);
      return;
    }

    setShowExplanation(true);

    try {
      await onExplain?.();
    } catch {
      setShowExplanation(false);
    }
  }, [onExplain, showExplanation]);

  return (
    <Resource
      style={style}
      className={classnames(showExplanation && styles['ai'], className)}
      testId={testId}
    >
      <QuestionToolbar
        bookmarked={bookmarked}
        onBookmark={toggleBookmark}
        options={
          <>
            {onFeedback && <FeedbackButton onFeedback={onFeedback} />}
            {onExplain && (
              <ExplanationButton
                shown={showExplanation}
                onToggle={handleShowExplanation}
              />
            )}
            {onPush && <PushQuestionButton onPush={onPush} />}
            {onEdit && <EditQuestionButton onEdit={onEdit} />}
          </>
        }
        actions={showExplanation && <AiWarningIcon />}
        close={onClose && { onClose }}
      />
      <ResourceBody key={question.id}>
        <Answer
          question={question}
          showReason={showReason}
          showExplanation={showExplanation}
        />
      </ResourceBody>
      <AnswerFooter
        question={question}
        showReason={showReason}
        onShowReason={handleShowReason}
      >
        {children}
      </AnswerFooter>
    </Resource>
  );
}

export { MultipleChoiceQuestionAnswer };
